import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const NOTE_WIDTH = 288;

/**
 * A cell value that shows which figure it used only when clicked (or Enter /
 * Space), not on hover: extra detail for someone checking a number. Closes on
 * a second click, Escape, a click elsewhere, or scrolling. The note renders on
 * the page body so the table's scroll container cannot clip it.
 */
export default function FigureNote({ note, children }: { note: string; children: ReactNode }) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const noteRef = useRef<HTMLSpanElement>(null);
  const open = pos !== null;

  useEffect(() => {
    if (!open) return;
    const close = () => setPos(null);
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (buttonRef.current?.contains(t) || noteRef.current?.contains(t)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const toggle = () => {
    if (open) {
      setPos(null);
      return;
    }
    const r = buttonRef.current?.getBoundingClientRect();
    if (!r) return;
    // Right-aligned under the value, kept inside the viewport.
    const left = Math.max(8, Math.min(r.right - NOTE_WIDTH, window.innerWidth - NOTE_WIDTH - 8));
    setPos({ top: r.bottom + 4, left });
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-label={open ? 'Hide which figure was used' : 'Show which figure was used'}
        onClick={(e) => {
          // The row may have its own click handler.
          e.stopPropagation();
          toggle();
        }}
        className="underline decoration-dotted decoration-accent underline-offset-2 cursor-pointer"
      >
        {children}
      </button>
      {pos
        ? createPortal(
            <span
              ref={noteRef}
              role="note"
              style={{ top: pos.top, left: pos.left, width: NOTE_WIDTH }}
              className="fixed z-dropdown whitespace-normal text-left text-xs font-normal leading-snug rounded-md border border-border bg-popover text-foreground p-2 shadow-lg"
            >
              {note}
            </span>,
            document.body,
          )
        : null}
    </>
  );
}
